import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MODAL_OVERLAY_Z_INDEX } from '../lib/overlayLayers';
import type { MessageAttachment } from '../lib/threads';

const buttonStyle: React.CSSProperties = {
  minHeight: 'var(--tap-target)', minWidth: 'var(--tap-target)',
  border: '1px solid var(--line)', borderRadius: 'var(--radius-pill)',
  background: 'var(--white)', color: 'var(--ink)', padding: '8px 14px',
  font: 'inherit', fontWeight: 700, cursor: 'pointer',
};

let viewerHistorySequence = 0;

/** Photos are prefiltered through the caller's matching Hub-media allowlist. */
export function MessagePhotoViewer({ photos, index, onChange, onClose, saveControl }: {
  photos: MessageAttachment[];
  index: number;
  onChange: (index: number) => void;
  onClose: () => void;
  saveControl: ReactNode;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const actionsRef = useRef({ index, onChange, onClose, count: photos.length });
  const touchRef = useRef<{ x: number; y: number } | null>(null);
  const historyTokenRef = useRef('');
  if (!historyTokenRef.current) historyTokenRef.current = `message-photo-viewer-${++viewerHistorySequence}`;
  const historyEntryActiveRef = useRef(false);
  const historyClosePendingRef = useRef(false);
  const historyUnwindRef = useRef(0);
  const [failedPhotoId, setFailedPhotoId] = useState<string | null>(null);
  useEffect(() => { actionsRef.current = { index, onChange, onClose, count: photos.length }; });
  const requestClose = () => {
    const token = historyTokenRef.current;
    if (historyEntryActiveRef.current && window.history.state?.messagePhotoViewer === token) {
      // The popstate handler below owns the actual close. This consumes only
      // the same-URL sentinel we added, rather than navigating away from the
      // page underneath the viewer.
      historyEntryActiveRef.current = false;
      historyClosePendingRef.current = true;
      window.history.back();
      return;
    }
    if (historyClosePendingRef.current) return;
    historyEntryActiveRef.current = false;
    actionsRef.current.onClose();
  };
  useEffect(() => {
    if (historyUnwindRef.current) {
      window.clearTimeout(historyUnwindRef.current);
      historyUnwindRef.current = 0;
    }
    const priorFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const token = historyTokenRef.current;
    // The ref survives React StrictMode's development effect replay, so one
    // open creates one Back entry, not two.
    if (!historyEntryActiveRef.current) {
      const priorState = window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};
      window.history.pushState({ ...priorState, messagePhotoViewer: token }, '');
      historyEntryActiveRef.current = true;
    }
    const onPopState = () => {
      if (!historyEntryActiveRef.current && !historyClosePendingRef.current) return;
      historyEntryActiveRef.current = false;
      historyClosePendingRef.current = false;
      actionsRef.current.onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      const current = actionsRef.current;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); requestClose(); }
      if (event.key === 'ArrowLeft' && current.index > 0) { event.preventDefault(); current.onChange(current.index - 1); }
      if (event.key === 'ArrowRight' && current.index + 1 < current.count) { event.preventDefault(); current.onChange(current.index + 1); }
      if (event.key !== 'Tab') return;
      const dialog = dialogRef.current;
      const targets = Array.from(dialog?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex="0"]') ?? [])
        .filter(element => element.getClientRects().length > 0);
      const first = targets[0];
      const last = targets[targets.length - 1];
      if (!first || !last) { event.preventDefault(); dialog?.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !dialog?.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog?.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    // Capture Escape before the expandable inbox row underneath can handle it.
    window.addEventListener('popstate', onPopState);
    document.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = priorOverflow;
      if (priorFocus?.isConnected) priorFocus.focus();
      // Not every close is a tap. The studio recalls the message, a post is
      // deleted, the list re-filters, and the viewer unmounts with its
      // sentinel still on the stack. Every stranded entry is one press of
      // Back that does nothing, for the rest of the session, and Back is the
      // button people actually press on a phone.
      //
      // Deferred by a tick, and cancelled at the top of this effect, because
      // StrictMode replays cleanup and setup back to back in development: a
      // synchronous pop there would unwind the entry the replay just pushed
      // and close the viewer on open. The token check covers the other
      // direction, an unmount that something else has already navigated past.
      if (!historyEntryActiveRef.current) return;
      historyUnwindRef.current = window.setTimeout(() => {
        historyUnwindRef.current = 0;
        if (!historyEntryActiveRef.current) return;
        if (window.history.state?.messagePhotoViewer !== token) return;
        historyEntryActiveRef.current = false;
        window.history.back();
      }, 0);
    };
  }, []);
  const photo = photos[index];
  if (!photo) return null;
  const photoFailed = failedPhotoId === photo.id;
  return createPortal(
    <div data-message-photo-viewer="true"
      style={{ position: 'fixed', inset: 0, zIndex: MODAL_OVERLAY_Z_INDEX, background: 'var(--scrim)', display: 'grid', placeItems: 'center', padding: 'var(--space-3)' }}
      onMouseDown={event => { if (event.target === event.currentTarget) requestClose(); }}>
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-label="Message photos" tabIndex={-1}
        style={{ width: 'min(100%, 1000px)', maxHeight: '100%', display: 'flex', flexDirection: 'column', gap: 12,
          borderRadius: 'var(--radius-lg)', padding: 'var(--space-3)', background: 'var(--white)', color: 'var(--ink)', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span aria-live="polite" style={{ flex: 1, overflowWrap: 'anywhere' }}>{photo.name || 'Photo'} ({index + 1} of {photos.length})</span>
          <button ref={closeRef} type="button" aria-label="Close photos" style={buttonStyle} onClick={requestClose}>X</button>
        </div>
        {/*
          The swipe lives on the stage, not on the <img>. A photo that failed
          to load is display:none, and that is exactly the one somebody wants
          to swipe past; handlers on the image gave it no hit area at all.
        */}
        <div data-photo-stage="true"
          style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', touchAction: 'pan-y pinch-zoom' }}
          onTouchStart={event => { touchRef.current = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null; }}
          onTouchMove={event => { if (event.touches.length !== 1) touchRef.current = null; }}
          onTouchCancel={() => { touchRef.current = null; }}
          onTouchEnd={event => {
            const start = touchRef.current;
            touchRef.current = null;
            if (!start || event.changedTouches.length !== 1 || event.touches.length !== 0) return;
            const dx = event.changedTouches[0].clientX - start.x;
            const dy = event.changedTouches[0].clientY - start.y;
            if (Math.abs(dx) < 50 || Math.abs(dx) <= Math.abs(dy)) return;
            const next = index + (dx < 0 ? 1 : -1);
            // One photo pages nowhere, in either direction.
            if (next >= 0 && next < photos.length) onChange(next);
          }}>
          {/*
            Sized from the box it is actually in, never from vh. On a phone
            the fixed overlay is the SMALL viewport and 100vh is the large
            one, so vh maths put Close above the top of the screen and Save
            below the fold, in a panel whose own overflow could not scroll
            because that was measured in vh too.
          */}
          <img key={photo.id} src={photo.url} alt={photo.name || 'Message photo'} draggable={false}
            style={{ display: photoFailed ? 'none' : 'block', maxWidth: '100%', maxHeight: '100%', minHeight: 0, objectFit: 'contain' }}
            onLoad={() => setFailedPhotoId(current => current === photo.id ? null : current)}
            onError={() => setFailedPhotoId(photo.id)} />
          {photoFailed && (
            <div role="alert" style={{ width: '100%', minHeight: 120, display: 'grid', placeItems: 'center', padding: 'var(--space-4)', textAlign: 'center', color: 'var(--rose-deep)', background: 'var(--silver-panel)', borderRadius: 'var(--radius-md)' }}>
              This photo could not load. Close it or try another photo.
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          {saveControl}
          {photos.length > 1 && <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={buttonStyle} disabled={index === 0} onClick={() => onChange(index - 1)}>Previous photo</button>
            <button type="button" style={buttonStyle} disabled={index === photos.length - 1} onClick={() => onChange(index + 1)}>Next photo</button>
          </div>}
        </div>
      </section>
    </div>, document.body,
  );
}
